import { useState, useEffect, useRef } from 'react';
import octopusInstance from '../../services/octopus/octopusInstance';
import { getExchangeCode } from '../../services/exchanges';
import { updateRowData, initializeDynamicRows } from './OptionDataUtils';

/**
 * Manages all data concerns for the Option Chain table:
 * - initializes dynamic rows from arrRows (with WS-data merge on strike changes)
 * - subscribes to DetailMarketDataMessage + GreekData WebSockets for each strike
 * - subscribes to CompactMarketDataMessage for the underlying spot price
 */
export function useOptionChainData({ arrRows, exchange, underlyingToken, underlyingLtp }) {
  const [dynamicRows, setDynamicRows] = useState([]);
  const [spotPrice, setSpotPrice] = useState(underlyingLtp || 0);
  const spotPriceWsHandler = useRef(null);

  // ── Initialize / merge dynamic rows when arrRows changes ──
  useEffect(() => {
    setDynamicRows(prevRows => {
      const initializedRows = initializeDynamicRows(arrRows, exchange);

      if (!prevRows || prevRows.length === 0) return initializedRows;

      // Preserve live WebSocket data for strikes that were already rendered
      return initializedRows.map(newRow => {
        const existingRow = prevRows.find(r => r.strike_price === newRow.strike_price);
        if (existingRow) {
          return {
            ...newRow,
            ce_data: {
              ...newRow.ce_data,
              ltp: existingRow.ce_data?.ltp !== undefined ? existingRow.ce_data.ltp : newRow.ce_data?.ltp,
              bestBidPrice: existingRow.ce_data?.bestBidPrice !== undefined ? existingRow.ce_data.bestBidPrice : newRow.ce_data?.bestBidPrice,
              bestAskPrice: existingRow.ce_data?.bestAskPrice !== undefined ? existingRow.ce_data.bestAskPrice : newRow.ce_data?.bestAskPrice
            },
            pe_data: {
              ...newRow.pe_data,
              ltp: existingRow.pe_data?.ltp !== undefined ? existingRow.pe_data.ltp : newRow.pe_data?.ltp,
              bestBidPrice: existingRow.pe_data?.bestBidPrice !== undefined ? existingRow.pe_data.bestBidPrice : newRow.pe_data?.bestBidPrice,
              bestAskPrice: existingRow.pe_data?.bestAskPrice !== undefined ? existingRow.pe_data.bestAskPrice : newRow.pe_data?.bestAskPrice
            },
            ce_ltp: existingRow.ce_ltp !== undefined ? existingRow.ce_ltp : newRow.ce_ltp,
            pe_ltp: existingRow.pe_ltp !== undefined ? existingRow.pe_ltp : newRow.pe_ltp,
            ce_close: existingRow.ce_close !== undefined ? existingRow.ce_close : newRow.ce_close,
            pe_close: existingRow.pe_close !== undefined ? existingRow.pe_close : newRow.pe_close,
            ce_oi: existingRow.ce_oi !== undefined ? existingRow.ce_oi : newRow.ce_oi,
            pe_oi: existingRow.pe_oi !== undefined ? existingRow.pe_oi : newRow.pe_oi,
            ce_oi_change_percent: existingRow.ce_oi_change_percent !== undefined ? existingRow.ce_oi_change_percent : newRow.ce_oi_change_percent,
            pe_oi_change_percent: existingRow.pe_oi_change_percent !== undefined ? existingRow.pe_oi_change_percent : newRow.pe_oi_change_percent,
            ce_volume: existingRow.ce_volume !== undefined ? existingRow.ce_volume : newRow.ce_volume,
            pe_volume: existingRow.pe_volume !== undefined ? existingRow.pe_volume : newRow.pe_volume,
            ce_change: existingRow.ce_change !== undefined ? existingRow.ce_change : newRow.ce_change,
            pe_change: existingRow.pe_change !== undefined ? existingRow.pe_change : newRow.pe_change,
            ce_abs_change: existingRow.ce_abs_change !== undefined ? existingRow.ce_abs_change : newRow.ce_abs_change,
            pe_abs_change: existingRow.pe_abs_change !== undefined ? existingRow.pe_abs_change : newRow.pe_abs_change,
            ce_delta: existingRow.ce_delta !== undefined ? existingRow.ce_delta : newRow.ce_delta,
            pe_delta: existingRow.pe_delta !== undefined ? existingRow.pe_delta : newRow.pe_delta,
            ce_gamma: existingRow.ce_gamma !== undefined ? existingRow.ce_gamma : newRow.ce_gamma,
            pe_gamma: existingRow.pe_gamma !== undefined ? existingRow.pe_gamma : newRow.pe_gamma,
            ce_theta: existingRow.ce_theta !== undefined ? existingRow.ce_theta : newRow.ce_theta,
            pe_theta: existingRow.pe_theta !== undefined ? existingRow.pe_theta : newRow.pe_theta,
            ce_vega: existingRow.ce_vega !== undefined ? existingRow.ce_vega : newRow.ce_vega,
            pe_vega: existingRow.pe_vega !== undefined ? existingRow.pe_vega : newRow.pe_vega,
            ce_iv: existingRow.ce_iv !== undefined ? existingRow.ce_iv : newRow.ce_iv,
            pe_iv: existingRow.pe_iv !== undefined ? existingRow.pe_iv : newRow.pe_iv,
            ce_rho: existingRow.ce_rho !== undefined ? existingRow.ce_rho : newRow.ce_rho,
            pe_rho: existingRow.pe_rho !== undefined ? existingRow.pe_rho : newRow.pe_rho,
          };
        }
        return newRow;
      });
    });
  }, [arrRows, exchange]);

  // ── WebSocket subscriptions per strike (market data + greeks) ──
  useEffect(() => {
    if (!Array.isArray(arrRows) || arrRows.length === 0) return;

    const updateRow = (index, side, msg) => {
      setDynamicRows(prev => {
        if (!prev[index]) return prev;
        const newRow = updateRowData(prev[index], side, msg);
        if (newRow === prev[index]) return prev;
        const newRows = [...prev];
        newRows[index] = newRow;
        return newRows;
      });
    };

    const handlers = arrRows.map((row, index) => {
      const ceHandler = row.call_option?.token && octopusInstance.wsHandler({
        messageType: "DetailMarketDataMessage",
        subscriptionLocation: `callOption-${row.call_option.exchange}-${row.call_option.token}`,
        payload: {
          exchangeCode: getExchangeCode(row.call_option.exchange),
          instrumentToken: row.call_option.token
        }
      });

      const peHandler = row.put_option?.token && octopusInstance.wsHandler({
        messageType: "DetailMarketDataMessage",
        subscriptionLocation: `putOption-${row.put_option.exchange}-${row.put_option.token}`,
        payload: {
          exchangeCode: getExchangeCode(row.put_option.exchange),
          instrumentToken: row.put_option.token
        }
      });

      const ceGreekHandler = row.call_option?.token && octopusInstance.wsHandler({
        messageType: "GreekData",
        subscriptionLocation: `callOptionGreek-${row.call_option.exchange}-${row.call_option.token}`,
        payload: {
          exchangeCode: getExchangeCode(row.call_option.exchange),
          instrumentToken: parseInt(row.call_option.token, 10)
        }
      });

      const peGreekHandler = row.put_option?.token && octopusInstance.wsHandler({
        messageType: "GreekData",
        subscriptionLocation: `putOptionGreek-${row.put_option.exchange}-${row.put_option.token}`,
        payload: {
          exchangeCode: getExchangeCode(row.put_option.exchange),
          instrumentToken: parseInt(row.put_option.token, 10)
        }
      });

      if (ceHandler) ceHandler.subscribe(({ msg }) => updateRow(index, 'ce', msg));
      if (peHandler) peHandler.subscribe(({ msg }) => updateRow(index, 'pe', msg));
      if (ceGreekHandler) ceGreekHandler.subscribe(({ msg }) => updateRow(index, 'ce', msg));
      if (peGreekHandler) peGreekHandler.subscribe(({ msg }) => updateRow(index, 'pe', msg));

      return { ceHandler, peHandler, ceGreekHandler, peGreekHandler };
    });

    return () => {
      handlers.forEach(({ ceHandler, peHandler, ceGreekHandler, peGreekHandler }) => {
        if (ceHandler?.unsubscribe) ceHandler.unsubscribe();
        if (peHandler?.unsubscribe) peHandler.unsubscribe();
        if (ceGreekHandler?.unsubscribe) ceGreekHandler.unsubscribe();
        if (peGreekHandler?.unsubscribe) peGreekHandler.unsubscribe();
      });
    };
  }, [arrRows, exchange]);

  // ── Sync prop LTP into state ──
  useEffect(() => {
    if (underlyingLtp) setSpotPrice(underlyingLtp);
  }, [underlyingLtp]);

  // ── Spot price WebSocket ──
  useEffect(() => {
    if (!underlyingToken) return;

    if (spotPriceWsHandler.current) {
      spotPriceWsHandler.current.unsubscribe();
      spotPriceWsHandler.current = null;
    }

    const handler = octopusInstance.wsHandler({
      messageType: "CompactMarketDataMessage",
      subscriptionLocation: Math.random().toString(),
      payload: {
        exchangeCode: getExchangeCode(exchange === "NFO" ? "NSE" : (exchange === "BFO" ? "BSE" : exchange)),
        instrumentToken: underlyingToken
      }
    });

    handler.subscribe(({ msg }) => {
      const newLtp = msg.ltp || msg.lastTradedPrice;
      if (newLtp && newLtp !== 0) setSpotPrice(newLtp);
    }).catch(error => {
      console.error("Error subscribing to spot price WebSocket:", error);
    });

    spotPriceWsHandler.current = handler;

    return () => {
      if (spotPriceWsHandler.current) {
        spotPriceWsHandler.current.unsubscribe().catch(error => {
          console.warn("Error unsubscribing from spot price WebSocket:", error);
        });
        spotPriceWsHandler.current = null;
      }
    };
  }, [exchange, underlyingToken]);

  return { dynamicRows, spotPrice };
}
